/**
 * Rocket Read's clock — per word, and adaptive (specs/rocket-read-mode.md §5).
 *
 * One fixed schedule cannot serve a first-grader at 20 words a minute and a fluent ten-year-old at
 * 150: the first is lost by round 10 and the second is bored until round 25. So the clock keeps an
 * estimate of THIS child's pace and glides the budget from the same generous start to that pace by
 * round `R`, then keeps tightening past it. What gets scaled is the distance each reader covers, not
 * the time: a fast reader's clock shrinks fast, a slow reader's slowly, and both runs get hard
 * around rounds 15–20 (§5.4, checked by adaptive.test.ts).
 *
 * Phaser-free, so the curve and the estimator are tested without a canvas.
 */
import { cellCount } from '@/game/maze/segment';

/** Every child's round 1: 60 s for nine words, the pack's median sentence. */
export const B_1 = 60 / 9;
/** Hard floor: never under 2 s for nine words (270 wpm). */
export const B_MIN = 2 / 9;
/** The round at which the budget equals the child's own pace. */
export const R = 13;
/** Per-round tightening after R. */
export const POST = 0.93;
/** Before any reading has been seen: 60 wpm, a second- or third-grader. */
export const PRIOR_PACE = 1.0;
/** How far one observation moves the estimate, in log space. */
export const ALPHA = 0.35;
/** The most one observation may move the estimate: ×2 or ÷2. */
const MAX_STEP = Math.log(2);

/** Seconds per word the child gets in round `round`, for an estimated pace of `pace` s/word. */
export function budgetPerWord(round: number, pace: number): number {
  const r = Math.max(1, Math.floor(round));
  const target = Math.min(B_1, Math.max(B_MIN, pace));
  const b = r <= R
    ? B_1 * (target / B_1) ** ((r - 1) / (R - 1))
    : target * POST ** (r - R);
  return Math.max(B_MIN, b);
}

/**
 * Words in a sentence: whitespace-separated chunks. A hyphenated compound and a numeral count as
 * one, punctuation as nothing.
 */
export function countWords(sentence: string): number {
  const t = sentence.trim();
  return t ? t.split(/\s+/).length : 0;
}

/** Nine words — the German pack's median sentence, and what `B_1` and `B_MIN` are quoted for. */
export const MEDIAN_UNITS = 9;

/**
 * What one "word" of budget is in each language (specs/rocket-read-mode.md §5.6, §10).
 *
 * The clock is quoted in seconds per word, and a word is not the same amount of reading
 * everywhere. Seven Latin packs write a nine-word sentence; Korean writes the same sentence in six
 * *eojeol* and Japanese in five *bunsetsu*, each of which carries more; Thai writes it with no
 * spaces at all, so there is nothing to count but clusters.
 *
 * So each language declares how it is measured and what its pack's MEDIAN sentence measures, and a
 * unit is scaled to `MEDIAN_UNITS / medianRaw`. The effect: the median sentence of every language
 * gets the same 60 s in round 1 and the same 2 s floor, and everything in between is that
 * language's own reading, not a translation of German word counts.
 *
 * `medianRaw` is measured from the shipped packs and pinned by `units.test.ts`, so rewriting a
 * pack's sentences cannot silently re-time the mode.
 */
export interface LangUnits {
  /** `words` = whitespace chunks; `cells` = grapheme clusters (maze/segment.ts), for unspaced scripts. */
  measure: 'words' | 'cells';
  /** The median sentence of that language's Comprehension pack, in `measure`. */
  medianRaw: number;
}

export const LANG_UNITS: Readonly<Record<string, LangUnits>> = {
  'de-CH': { measure: 'words', medianRaw: 9 },
  de: { measure: 'words', medianRaw: 9 },
  en: { measure: 'words', medianRaw: 9 },
  nl: { measure: 'words', medianRaw: 9 },
  fr: { measure: 'words', medianRaw: 9 },
  es: { measure: 'words', medianRaw: 9 },
  it: { measure: 'words', medianRaw: 9 },
  // Spaces separate eojeol, so a Korean "word" is a noun plus its particle — fewer, and each one
  // more to read.
  ko: { measure: 'words', medianRaw: 6 },
  // Spaces separate bunsetsu, and this pack is written for children, who get them.
  ja: { measure: 'words', medianRaw: 5 },
  // No spaces anywhere: the only honest unit is the cluster a Thai reader takes in.
  th: { measure: 'cells', medianRaw: 22 },
};

export function unitsFor(lang: string): LangUnits {
  return LANG_UNITS[lang] ?? LANG_UNITS[lang.split('-')[0]] ?? { measure: 'words', medianRaw: MEDIAN_UNITS };
}

/**
 * The length of a sentence in budget units — what `budgetPerWord` is multiplied by. One unit is one
 * word of a Latin sentence, and the equivalent share of a sentence in any other language.
 */
export function readingUnits(sentence: string, lang: string): number {
  const u = unitsFor(lang);
  const raw = u.measure === 'cells' ? cellCount(sentence, lang) : countWords(sentence);
  return (raw * MEDIAN_UNITS) / u.medianRaw;
}

/** How a round's reading ended, for the estimator. */
export interface RoundOutcome {
  /** True when the child tapped "done"; false when the rocket arrived first. */
  tapped: boolean;
  correct: boolean;
  /** Time from the clock starting to the tap (or the timeout), in ms. */
  readMs: number;
  /** The sentence's length in budget units (readingUnits), NOT its raw word count. */
  words: number;
  /** The per-word budget this round had, in s/word. */
  budget: number;
}

/**
 * The next pace estimate after one round (§5.3):
 *
 * | tap, right     | a real reading: move toward readMs / words              |
 * | tap, wrong     | nothing reliable (tapped too early, or misread)         |
 * | timeout, right | copes with this budget: move toward it if it is faster  |
 * | timeout, wrong | needed more than this budget: at least 1.1 × it         |
 *
 * Only a tap FOLLOWED BY a right answer counts as a speed reading — that is what stops a child who
 * taps through and guesses from dragging the clock down, since two guesses in three are wrong. The
 * step clamp stops one lucky guess from halving the estimate.
 */
export function updatePace(pace: number, o: RoundOutcome): number {
  if (o.words <= 0) return pace;
  if (o.tapped && o.correct) {
    const seen = Math.max(B_MIN, o.readMs / 1000 / o.words);
    return towards(pace, seen);
  }
  if (!o.tapped && o.correct) return o.budget < pace ? towards(pace, o.budget) : pace;
  if (!o.tapped && !o.correct) return Math.max(pace, 1.1 * o.budget);
  return pace;
}

function towards(pace: number, seen: number): number {
  const step = ALPHA * (Math.log(seen) - Math.log(pace));
  return Math.exp(Math.log(pace) + Math.max(-MAX_STEP, Math.min(MAX_STEP, step)));
}

/**
 * The run's clock: the pace estimate plus the budget it implies. `p̂` lives for one run only
 * (§5.3) — the early rounds are nearly free, so relearning costs nothing.
 */
export class RocketClock {
  pace = PRIOR_PACE;

  /** Read time in round `round` for a sentence of `units` budget units (readingUnits), in ms. */
  readMs(round: number, units: number): number {
    return Math.round(budgetPerWord(round, this.pace) * units * 1000);
  }

  budget(round: number): number {
    return budgetPerWord(round, this.pace);
  }

  record(o: RoundOutcome): void {
    this.pace = updatePace(this.pace, o);
  }
}

/**
 * The Ready card's time label (§5.5): whole seconds, one decimal place below 10 s — rounded UP, so
 * the card never promises less time than the clock gives. Formatted for `lang` ("7,5" in German).
 */
export function formatSeconds(ms: number, lang: string): string {
  const s = ms / 1000;
  const digits = s >= 10 ? 0 : 1;
  return new Intl.NumberFormat(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits })
    .format(displayedSeconds(ms));
}

/**
 * The number `formatSeconds` will render for `ms`, before it is turned into text — so a caller can
 * ask "has the readout changed?" without paying for the answer.
 *
 * Formatting is not cheap: it builds an `Intl.NumberFormat` every call. A countdown driven from a
 * tween's onUpdate asks 60 times a second for a value that changes at most 10 times a second, so
 * the guard is worth more than the rounding it repeats — and it lives here, beside the rule it has
 * to agree with, rather than being restated at the call site where the two could drift apart.
 */
export function displayedSeconds(ms: number): number {
  const s = ms / 1000;
  const digits = s >= 10 ? 0 : 1;
  return Math.ceil(s * 10 ** digits) / 10 ** digits;
}
